<!--
    nav menu side panel for mobile version
    <MobileNavDrawerContent

    Emits:
    - @updateCurrentMenu    # Fired on change of currently displayed menu
    - @close                # Fired when side panel needs to be closed
    />
-->
<script setup lang="ts">
/* UTILS */
import { computed, ref, watch } from 'vue';

import ProfileCard from '@/components/profileCard/ProfileCard.vue';
/* COMPONENTS */
import NavMenuItemList from '@/features/navbar/NavMenuItemList.vue';
import NavTrialDays from '@/features/navbar/NavTrialDays.vue';
/* HELPERS */
import { getMenu } from '@/helpers/navigation';
import { getSpacesRole } from '@/helpers/spaces';
/* STORES */
import { useAuthStore } from '@/stores/auth';
/* TYPES */
import type { NavItemType } from '@/types/navigation/navigation.types';

import SpacesMenu from './SpacesMenu.vue';

const emits = defineEmits(['updateCurrentMenu', 'close']);
// VARIABLES
/* MENUS */
const navMenu = ref(getMenu('mobileNavMenu'));
const userMenu = ref(getMenu('userMenu'));
const auth = useAuthStore().getFullUser;
const isTrial = useAuthStore().getCompany.is_trial;
const profileCardElement = ref();

const menuType = computed(() => (currentMenu.value ? 'submenu' : 'menu')); // Type of menu currently being displayed - depending on whether or not there is a currently selected menu
const currentMenu = ref<NavItemType | undefined>(); // Currently displayed menu - implies the displayed items are the submenus of this menu
const hasSpaces = computed(() => getSpacesRole() === 'validSpaces' || getSpacesRole() === 'invalidSpaces');

// FUNCTIONS
/**
 * Called on menu item click that triggers an action and not a routing - switches display to allow viewing submenu items for the clicked menu item
 * @param {NavItemType} item item that was clicked and that triggers opening a submenu
 */
function toggleMenu(item) {
  currentMenu.value = item;
}

/**
 * Called on click on the back chevron, allows to reset display
 */
function reset() {
  currentMenu.value = undefined;
}

function redirectToProfile() {
  profileCardElement.value?.redirectToProfile();
  emits('close');
}

/** WATCHERS */
// On selected menu change, fire an emit
watch(
  () => currentMenu.value,
  () => {
    emits('updateCurrentMenu', currentMenu.value);
  },
  {
    immediate: true
  }
);

/* EXPOSED */
defineExpose({
  reset
});
</script>

<template>
  <v-window v-model="menuType">
    <!-- MAIN MENU -->
    <v-window-item value="menu">
      <!-- HEADER -->
      <v-list class="pt-0">
        <v-list-item class="cursor-pointer" @click="redirectToProfile">
          <ProfileCard ref="profileCardElement" :user="auth" avatarSize="l" noJob class="w-100 py-3" />
          <NavTrialDays v-if="isTrial" class="mb-3" />
        </v-list-item>
        <SpacesMenu v-if="hasSpaces" />
        <v-divider />
        <!-- NAV MENU -->
        <NavMenuItemList :items="navMenu" @itemClick="toggleMenu" @close="emits('close')" />
        <v-divider />
        <!-- USER MENU -->
        <NavMenuItemList :items="userMenu" @itemClick="toggleMenu" @close="emits('close')" />
      </v-list>
    </v-window-item>
    <!-- SUB MENU -->
    <v-window-item value="submenu">
      <NavMenuItemList
        v-if="currentMenu?.items"
        :items="currentMenu.items"
        @itemClick="toggleMenu"
        @close="emits('close')"
      />
    </v-window-item>
  </v-window>
</template>
